import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProperties } from "@/lib/api";
import { Bed, Bath, Maximize, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Chatbot from "@/components/Chatbot";
import SectionHeading from "@/components/SectionHeading";

const fmtAED = (n) => `AED ${(n / 1_000_000).toFixed(1)}M`;
const fmtUSD = (n) => `USD ${(n / 1_000_000).toFixed(1)}M`;

const TYPES = ["All", "Villa", "Penthouse", "Mansion", "Apartment"];

export default function ListingsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => (filter === "All" ? items : items.filter((p) => p.type === filter)),
    [items, filter]
  );

  return (
    <main data-testid="listings-page" className="bg-[#f5f0e8] text-[#1c1c1c] grain">
      <Navbar solid />

      <section className="pt-32 lg:pt-40 pb-16 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              The full<br /><em className="not-italic text-[#3d2b1f]">collection</em>.
            </h1>
            <span className="underline-label mt-7">Listings · 2026</span>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[#3d2b1f] text-base leading-relaxed">
              Every residence presented below is held under an AZEFAC mandate. Off-market
              opportunities are released only on direct enquiry.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center justify-between gap-6 mb-12 pb-6 border-b border-[#1c1c1c]/15">
          <div className="flex flex-wrap gap-2" data-testid="listings-filter">
            {TYPES.map((t) => (
              <button
                key={t}
                data-testid={`listings-filter-${t.toLowerCase()}`}
                onClick={() => setFilter(t)}
                className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.28em] border transition-all duration-300 ${
                  filter === t
                    ? "border-[#c5a059] bg-[#c5a059] text-[#1c1c1c]"
                    : "border-[#1c1c1c]/20 text-[#1c1c1c]/70 hover:border-[#c5a059] hover:text-[#c5a059]"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-[#3d2b1f]">
            {loading ? "Curating…" : `${filtered.length} ${filtered.length === 1 ? "residence" : "residences"}`}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {filtered.map((p, i) => (
            <Link
              key={p.id}
              to={`/listings/${p.slug}`}
              data-testid={`listing-card-${p.slug}`}
              className="group block"
            >
              <div className="img-zoom aspect-[4/5] mb-6 bg-[#ede5d5]">
                <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="underline-label mb-2 text-[#c5a059]">{p.type} · {p.status}</span>
                  <h3 className="mt-3 font-display text-2xl lg:text-3xl text-[#1c1c1c] group-hover:text-[#3d2b1f] transition-colors">
                    {p.title}
                  </h3>
                  <div className="mt-2 flex items-center gap-2 text-[#3d2b1f] text-sm">
                    <MapPin size={13} className="text-[#c5a059]" />
                    {p.location}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-xl text-[#1c1c1c]">{fmtAED(p.price_aed)}</div>
                  <div className="text-[10px] tracking-[0.28em] text-[#3d2b1f] mt-1">{fmtUSD(p.price_usd)}</div>
                </div>
              </div>
              <div className="mt-5 flex items-center gap-6 text-[12px] uppercase tracking-[0.22em] text-[#3d2b1f]">
                <span className="flex items-center gap-2"><Bed size={13} className="text-[#c5a059]" /> {p.bedrooms}</span>
                <span className="flex items-center gap-2"><Bath size={13} className="text-[#c5a059]" /> {p.bathrooms}</span>
                <span className="flex items-center gap-2"><Maximize size={13} className="text-[#c5a059]" /> {p.size_sqft.toLocaleString()} sqft</span>
              </div>
              <div className="mt-5 h-px bg-[#1c1c1c]/10 group-hover:bg-[#c5a059] transition-colors" />
              <span className="underline-label mt-4">Enquire — 0{i + 1}</span>
            </Link>
          ))}
        </div>

        {!loading && filtered.length === 0 && (
          <div data-testid="listings-empty" className="py-20 text-center">
            <SectionHeading
              alignment="center"
              title={<>No residences match<br />this selection.</>}
              subtitle="Off-Market mandates may still be available — enquire."
            />
          </div>
        )}
      </section>

      <Footer />
      <Chatbot />
    </main>
  );
}
